// helpers/fetchVehicleSteps.js

import { getConfirmationReturnTo } from "./confirmationReturnTo";
import { isInteriorComplete } from "@/app/[id]/profil/fotos/fotosSectionStatus";
import { normalizeAustrianMobilePhone } from "@/lib/phone";

// ─────────────────────────────────────────────────────────────────────────────
// Damage fallback:
// Wenn es mindestens ein Bild für einen Schadenstyp gibt (z. B. "kratzer_1"),
// zählt dieser Typ als vorhanden/true – auch wenn carDamageState.<typ> fehlt
// oder false ist.
//
// Für "damagesDone" wird geprüft:
// 1) Damage-Questions komplett – aber nur die Fragen, die der Schäden-Hub
//    tatsächlich stellt (Außenschäden-Kategorien + Felgen-/Reifen-Check +
//    Warnleuchten + Nachlackierung + Zusatz, siehe HUB_DAMAGE_KEYS):
//    ✅ Key-aware: boolean-Keys zählen, aber auch:
//      - felgen (map) zählt als beantwortet, wenn alle 4 Felgen-Flags existieren
//      - reifen (map) zählt als beantwortet, wenn alle 4 Reifen-Flags existieren
//    + (Bild-abgeleitete Typen) als beantwortet
// 2) PLUS: Nachlackierung ist beantwortet (Ja/Nein/Nicht sicher)
//    und bei "Ja" muss Größe gesetzt sein.
// 3) NUR mechanische_oder_elektrische ist KEINE Pflicht mehr: die Frage wird
//    über die Fahrtüchtig-Frage (Letzte Details, gleicher Storage-Key)
//    beantwortet und darf den Foto-Step nicht blockieren – über den Hub ist
//    sie nicht mehr erreichbar.
//
// FIN-Logik:
// finUpdatedDone ist erledigt, sobald irgendwo eine FIN gesetzt ist
// (vehicleData.fin ODER fahrzeug_eigentum.fin) – ohne Formatprüfung.
// ─────────────────────────────────────────────────────────────────────────────

import { getBatteryOwner } from "@/helpers/getIsBatteryOwner";
import { isElectric } from "@/helpers/getIsElectric";

const NO_CABLE_TEXT = "Ich habe die Ladekabel nicht";
const shouldAskCableType = (v = {}) => {
  const own = (v?.fahrzeug_eigentum?.cable_ownership || "").trim();
  return isElectric(v) === true && own !== NO_CABLE_TEXT;
};

// ✅ This is the canonical set of your 10 damage “questions”
const BASE_DAMAGE_KEYS = [
  "felgen",
  "reifen",
  "windschutzscheibe",
  "kratzer",
  "dellen",
  "lackprobleme",
  "anbauteile",
  "warnleuchten",
  "mechanische_oder_elektrische",
  "zusatz",
];

// Pflicht für damagesDone: die Fragen des Schäden-Hubs (Außenschäden-
// Kategorien + Felgen/Reifen/Warnleuchten + Zusatz; Nachlackierung wird
// separat geprüft). Nur mechanische_oder_elektrische ist raus – Begründung
// siehe Kopf-Kommentar dieser Datei.
const HUB_DAMAGE_KEYS = BASE_DAMAGE_KEYS.filter(
  (k) => k !== "mechanische_oder_elektrische",
);

export const makeEmptyStatus = (id, isMobile = false) => ({
  personalInfosDone: { done: false, path: `/${id}/profil/details` },

  // 🔹 Sonderausstattungen als eigener (optional) Vehicle-Info-Step
  sonderausstattungenDone: {
    done: false,
    path: `/${id}/profil/merkmale-und-ausstattung/sonderausstattungen`,
  },

  aussenfarbeDone: {
    done: false,
    path: `/${id}/profil/merkmale-und-ausstattung/aussenfarbe`,
  },

  seatMaterialDone: {
    done: false,
    path: `/${id}/profil/merkmale-und-ausstattung/sitzmaterial`,
  },
  keyAmountDone: {
    done: false,
    path: `/${id}/profil/merkmale-und-ausstattung/auto-schluessel`,
  },
  smokedDone: {
    done: false,
    path: `/${id}/profil/merkmale-und-ausstattung/rauchen`,
  },

  previousOwnerDone: {
    done: false,
    path: `/${id}/profil/merkmale-und-ausstattung/vorbesitzer`,
  },
  pickerlDone: {
    done: false,
    path: `/${id}/profil/merkmale-und-ausstattung/pickerl`,
  },

  extraWheelsDone: {
    done: false,
    path: `/${id}/profil/merkmale-und-ausstattung/extra-raeder`,
  },

  fitToDriveDone: {
    done: false,
    path: `/${id}/profil/merkmale-und-ausstattung/fahrtuechtig`,
  },

  wearAndTearDone: {
    done: false,
    path: `/${id}/profil/merkmale-und-ausstattung/schaeden-gebrauchsspuren`,
  },

  fahrzeugHalterDone: {
    done: false,
    path: `/${id}/profil/fahrzeug-eigentum/fahrzeug-halter`,
  },
  finanzierungDone: {
    done: false,
    path: `/${id}/profil/fahrzeug-eigentum/finanzierung`,
  },
  serviceBookletDone: {
    done: false,
    path: `/${id}/profil/merkmale-und-ausstattung/serviceheft`,
  },
  standortDone: {
    done: false,
    path: `/${id}/profil/fahrzeug-eigentum/standort`,
  },

  // Reihenfolge = Foto-Flow (exterior -> felgen -> reifen -> interior):
  // Profil.js iteriert Object.entries(status) als "erster unvollständiger
  // Step"-Fallback, daher muss die Key-Reihenfolge dem Flow entsprechen.
  exteriorPhotosDone: {
    done: false,
    path: isMobile
      ? `/${id}/profil/fotos/fotos-schiessen/exterior`
      : `/${id}/profil/fotos`,
  },
  rimsPhotosDone: {
    done: false,
    path: isMobile
      ? `/${id}/profil/fotos/fotos-schiessen/felgen`
      : `/${id}/profil/fotos`,
  },
  wheelsPhotosDone: {
    done: false,
    path: isMobile
      ? `/${id}/profil/fotos/fotos-schiessen/reifen`
      : `/${id}/profil/fotos`,
  },
  interiorPhotosDone: {
    done: false,
    path: isMobile
      ? `/${id}/profil/fotos/fotos-schiessen/interior`
      : `/${id}/profil/fotos`,
  },

  // ✅ Damage step
  damagesDone: {
    done: false,
    path: `/${id}/profil/fotos/fotos-schiessen/schaeden`,
  },

  serviceHistoryDone: { done: false, path: `/${id}/profil/service-historie` },

  finUpdatedDone: { done: false, path: `/${id}/profil/fahrzeug-eigentum/fin` },
  telefonnummerDone: {
    done: false,
    path: `/${id}/profil/details/telefonnummer`,
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const keyToDamageType = (k) => (typeof k === "string" ? k.split("_")[0] : "");

// treat any non-"reset" status as completed profile
const isNonResetStatus = (vehicleData = {}) => {
  const s = String(vehicleData?.status ?? "")
    .trim()
    .toLowerCase();
  return s.length > 0 && s !== "reset";
};

const isPriceCalculationAutomated = (vehicleData = {}) =>
  vehicleData?.ab?.priceCalculationAutomated === true ||
  vehicleData?.priceCalculationAutomated === true;

const hasStartPrice = (vehicleData = {}) => {
  const values = [
    vehicleData?.minPrice,
    vehicleData?.price,
    vehicleData?.auction?.finalPrice,
  ];
  return values.some(
    (v) => v !== undefined && v !== null && String(v).trim() !== "",
  );
};

// FIN gilt als "gesetzt", wenn irgendein Wert vorhanden ist (ohne Formatprüfung)
const hasFinPresent = (vehicleData = {}) => {
  const candidates = [vehicleData?.fin, vehicleData?.fahrzeug_eigentum?.fin];
  return candidates.some(
    (v) => v !== undefined && v !== null && String(v).trim() !== "",
  );
};

const hasUserPhone = (userData = {}) => {
  const phone = normalizeAustrianMobilePhone(String(userData?.telefon || ""))?.e164;
  const smsVerified =
    userData?.sms?.smsVerified === true || userData?.smsVerified === true;
  return Boolean(phone) &&
    ((process.env.NEXT_PUBLIC_SMS_ENABLED !== "true" &&
      userData?.phoneFormatValidatedE164 === phone) || smsVerified);
};

const isServiceBookletQuestionDone = (vehicleCondition = {}) => {
  const serviceBooklet = vehicleCondition?.service_booklet;
  if (!serviceBooklet || typeof serviceBooklet !== "object") return false;
  if (serviceBooklet.hasBooklet === false) return true;
  if (serviceBooklet.hasBooklet === true) {
    return (
      typeof serviceBooklet.maintenance === "string" &&
      serviceBooklet.maintenance.length > 0
    );
  }
  return false;
};

// Nachlackierung counts as answered if:
// - Nein / Nicht sicher
// - Ja + size provided
const isNachlackierungDone = (vehicleData = {}) => {
  const val = vehicleData?.carDamageState?.nachlackierung;
  const size = vehicleData?.carDamageSize?.nachlackierung;

  return (
    val === "Nein" ||
    val === "Nicht sicher" ||
    (val === "Ja" && size != null && String(size).trim() !== "")
  );
};

const isMechanischeAnsweredByCondition = (vehicleData = {}) => {
  const fit = vehicleData?.vehicleCondition?.fitToDrive;
  if (typeof fit === "boolean") return true;

  const issues = vehicleData?.vehicleCondition?.fitToDriveIssues;
  return typeof issues === "string" && issues.trim() !== "";
};

/**
 * ✅ Key-aware check whether a damage "question" is answered in Firestore.
 * Your Firestore structure mixes:
 * - booleans (lackprobleme, kratzer, ...)
 * - maps (felgen, reifen)
 * - strings (mechanische_oder_elektrische)
 */
const isAnsweredDamageValue = (key, value) => {
  if (value === undefined || value === null) return false;

  // booleans: answered even if false
  if (typeof value === "boolean") return true;

  // strings: answered if not empty
  if (typeof value === "string") return value.trim() !== "";

  // objects/maps:
  if (typeof value === "object") {
    const obj = value || {};

    // felgen map must contain all 4 boolean flags
    if (key === "felgen") {
      const required = [
        "vordereFahrerfelge",
        "vordereBeifahrerfelge",
        "hintereFahrerfelge",
        "hintereBeifahrerfelge",
      ];
      return required.every((k) => typeof obj?.[k] === "boolean");
    }

    // reifen map must contain all 4 boolean flags
    if (key === "reifen") {
      const required = [
        "vordererFahrerreifen",
        "vordererBeifahrerreifen",
        "hintererFahrerreifen",
        "hintererBeifahrerreifen",
      ];
      return required.every((k) => typeof obj?.[k] === "boolean");
    }

    // any other object: treat as answered if it has at least one key
    return Object.keys(obj).length > 0;
  }

  // numbers etc. -> if present, treat as answered
  return true;
};

export const isAbFlowStarted = (vehicleData = {}) => {
  if (!isPriceCalculationAutomated(vehicleData)) return false;

  // Self-Valuation-Flow-Marker (nur bei NEUEN Autos in der Creation gesetzt):
  // diese Autos nutzen bewusst das NON-AB-Frageset (main-/Self-Valuation-Set,
  // weniger Fragen – kein Zweiter Radsatz/Fahrtuechtig/Gebrauchsspuren/Serviceheft).
  // Bestandsautos haben das Feld NICHT → sie fallen unveraendert durch die
  // bestehenden Heuristiken unten (forward-only, kein Eingriff in laufende Autos).
  // priceCalculationAutomated bleibt true, daher laufen Engine + Preisseite weiter.
  if (vehicleData?.ab?.selfValuationFlowOnly === true) return false;

  if (
    vehicleData?.priceCalculationQuestionsFinished === true ||
    vehicleData?.ab?.priceCalculationQuestionsFinished === true
  ) {
    return true;
  }

  if (vehicleData?.isCompleted === true || hasStartPrice(vehicleData)) {
    return true;
  }

  const vehicleFeatures = vehicleData?.vehicleFeatures || {};
  const vehicleCondition = vehicleData?.vehicleCondition || {};
  const bodyPaintValue =
    vehicleFeatures?.bodyPaint ??
    vehicleFeatures?.aussenfarbe ??
    vehicleFeatures?.außenfarbe ??
    vehicleData?.bodyPaint ??
    vehicleData?.aussenfarbe ??
    vehicleData?.außenfarbe;

  const extraReifenValue = vehicleFeatures?.extraReifen;
  const hasExtraWheelsSignal =
    typeof vehicleFeatures?.extraWheels === "boolean" ||
    (extraReifenValue !== undefined &&
      extraReifenValue !== null &&
      String(extraReifenValue).trim() !== "");

  const fitToDriveAnswered =
    typeof vehicleCondition?.fitToDrive === "boolean" ||
    isAnsweredDamageValue(
      "mechanische_oder_elektrische",
      vehicleData?.carDamageState?.mechanische_oder_elektrische,
    );

  const wearAndTearAnswered = Array.isArray(vehicleCondition?.wear_and_tear)
    ? vehicleCondition.wear_and_tear.length > 0
    : !!vehicleCondition?.wear_and_tear;

  return (
    !!bodyPaintValue ||
    hasExtraWheelsSignal ||
    fitToDriveAnswered ||
    wearAndTearAnswered ||
    isServiceBookletQuestionDone(vehicleCondition)
  );
};

/**
 * ✅ Builds the set of answered base damage keys (size 0..10).
 * Includes:
 * - carDamageState answers (key-aware)
 * - image fallback: "kratzer_1" -> "kratzer"
 */
const buildEffectiveDamageAnsweredSet = (vehicleData = {}) => {
  const rawState = vehicleData?.carDamageState || {};
  const images = vehicleData?.carDamages || {}; // { typ_index: url }

  const answered = new Set();

  // 1) from carDamageState, key-aware
  for (const k of BASE_DAMAGE_KEYS) {
    const v = rawState?.[k];
    if (isAnsweredDamageValue(k, v)) answered.add(k);
  }

  if (
    !answered.has("mechanische_oder_elektrische") &&
    isMechanischeAnsweredByCondition(vehicleData)
  ) {
    answered.add("mechanische_oder_elektrische");
  }

  // 2) image fallback: "kratzer_1" -> "kratzer"
  Object.keys(images || {})
    .map(keyToDamageType)
    .filter(Boolean)
    .forEach((t) => {
      if (BASE_DAMAGE_KEYS.includes(t)) answered.add(t);
    });

  return answered;
};

// mechanische_oder_elektrische ist bewusst NICHT mehr im Walk: die Frage
// wird über Fahrtüchtig (Letzte Details) beantwortet – sonst würde der
// Schäden-Walk unbeantwortete Hub-Besucher auf die alte Mechanik-Seite
// schicken. Die Seite selbst bleibt erreichbar (Deep-Link/Bestand).
const DAMAGE_FLOW_ORDER = [
  "felgen",
  "reifen",
  "windschutzscheibe",
  "kratzer",
  "dellen",
  "lackprobleme",
  "nachlackierung",
  "anbauteile",
  "warnleuchten",
  "zusatz",
];

const normalizeDamageBasePath = (value) =>
  String(value || "")
    .trim()
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");

const resolveDamageBasePath = (_id, damageBasePath) => {
  const explicit = normalizeDamageBasePath(damageBasePath);
  if (explicit) return explicit;

  return "fotos-schiessen/schaeden";
};

const getDamagePathMap = (id, damageBasePath = "fotos-schiessen/schaeden") => {
  const basePath = normalizeDamageBasePath(damageBasePath) || "fotos-schiessen/schaeden";
  const prefix = `/${id}/profil/fotos/${basePath}`;

  return {
    felgen: `${prefix}/felgenschaeden`,
    reifen: `${prefix}/reifen`,
    windschutzscheibe: `${prefix}/windschutzscheibe`,
    kratzer: `${prefix}/kratzer-oder-schrammen`,
    dellen: `${prefix}/dellen`,
    lackprobleme: `${prefix}/lackprobleme`,
    nachlackierung: `${prefix}/nachlackierung`,
    anbauteile: `${prefix}/anbauteile`,
    warnleuchten: `${prefix}/warnleuchten`,
    mechanische_oder_elektrische: `${prefix}/mechanische-oder-elektrische`,
    zusatz: `${prefix}/zusatz`,
  };
};

const areAllQuestionStepsDone = (status = {}) =>
  Object.entries(status).every(([key, step]) => {
    if (key === "personalInfosDone") return true;
    return step?.done === true;
  });

const VEHICLE_INFO_REQUIRED_KEYS = [
  "seatMaterialDone",
  "keyAmountDone",
  "smokedDone",
  "previousOwnerDone",
  "pickerlDone",
  "fahrzeugHalterDone",
  "finanzierungDone",
  "standortDone",
  "finUpdatedDone",
  "telefonnummerDone",
];
const VEHICLE_INFO_REQUIRED_KEYS_AB = [
  // aussenfarbe wird bewusst NICHT mehr abgefragt: Seite/Route/Status-Eintrag
  // bleiben erhalten, der Schritt ist nur aus Flow/Pflicht/Final/Progress entfernt.
  "seatMaterialDone",
  "keyAmountDone",
  "smokedDone",
  "previousOwnerDone",
  "pickerlDone",
  "fitToDriveDone",
  "wearAndTearDone",
  // extraWheels ist die ERSTE Letzte-Details-Frage (nach Zustand) – vorher
  // letzte Info-Frage bzw. Modal nach den Reifen-Fotos.
  "extraWheelsDone",
  "fahrzeugHalterDone",
  "finanzierungDone",
  "standortDone",
  "serviceBookletDone",
  "finUpdatedDone",
  "telefonnummerDone",
];

const VEHICLE_INFO_OPTIONAL_KEYS = ["sonderausstattungenDone"];
// Fuer non-AB/Self-Valuation werden diese Keys in checkVehicleSteps
// automatisch als done markiert (kein Pflicht-Progress). extraWheels und
// fitToDrive gehoeren NICHT mehr hierher: sie sind die ersten beiden
// Letzte-Details-Fragen ALLER Flows und bekommen echte Erledigt-
// Bedingungen aus den gespeicherten Antworten (sonst wuerde der
// Profil-Weiter-Button sie ueberspringen und die Kachel zu frueh als
// fertig zaehlen).
export const AB_ONLY_KEYS = [
  "aussenfarbeDone",
  "wearAndTearDone",
  "serviceBookletDone",
];

// Öffentlicher Export: alle Vehicle-Info-Keys, optional zuerst
export const VEHICLE_INFO_KEYS = [
  ...VEHICLE_INFO_OPTIONAL_KEYS,
  ...VEHICLE_INFO_REQUIRED_KEYS,
];

export const VEHICLE_INFO_GROUP_INFO_KEYS = [
  ...VEHICLE_INFO_OPTIONAL_KEYS,
  "seatMaterialDone",
  "keyAmountDone",
  "smokedDone",
  "previousOwnerDone",
  "pickerlDone",
];
export const VEHICLE_INFO_GROUP_INFO_KEYS_AB = [
  ...VEHICLE_INFO_OPTIONAL_KEYS,
  // aussenfarbe deaktiviert (nicht mehr im Flow) – s. VEHICLE_INFO_REQUIRED_KEYS_AB
  // extraWheels wandert als ERSTE Frage in die Letzte-Details-Gruppe.
  "seatMaterialDone",
  "keyAmountDone",
  "smokedDone",
  "previousOwnerDone",
  "pickerlDone",
];

export const VEHICLE_INFO_GROUP_CONDITION_KEYS = [];
export const VEHICLE_INFO_GROUP_CONDITION_KEYS_AB = [
  "fitToDriveDone",
  "wearAndTearDone",
];

export const POST_PRICE_DETAILS_KEYS = [
  "fahrzeugHalterDone",
  "finanzierungDone",
  "standortDone",
  "telefonnummerDone",
];
// EINE Quelle fuer die "Letzte Details"-Teilsequenz – von Vorwaerts
// (getNextVehicleInfoSequentialPath) UND Rueckwaerts
// (getPrevVehicleInfoSequentialPath) genutzt, damit Zurueck die exakte
// Umkehrung von Weiter ist. extraWheels (zweiter Radsatz) ist die ERSTE
// Letzte-Details-Frage (ersetzt das fruehere ExtraReifen-Modal nach den
// Reifen-Fotos – das fragte flow-unabhaengig jeden, daher auch hier fuer
// alle Flows). fitToDrive (Fahrtuechtig) folgt direkt danach: im AB-Flow
// lebt die Frage in der Zustand-Gruppe, fuer Self-Valuation/non-AB wird sie
// hier gestellt (Produkt-Entscheidung: Frage soll jeder Kunde sehen).
export const LAST_DETAILS_FLOW_KEYS = [
  "extraWheelsDone",
  "fitToDriveDone",
  "standortDone",
  "telefonnummerDone",
  "finUpdatedDone",
  "serviceHistoryDone",
];
// Completed-AB "Letzte Details": die Post-Price-Schritte kommen NACH Fotos.
// Reihenfolge = extraWheels (zweiter Radsatz) -> fahrzeugHalter ->
// finanzierung -> standort -> telefon -> fin -> service-historie. Eine Quelle
// fuer den Vor- (getNext) UND Rueckwaerts-Flow (getPrev) der Letzte-Details-
// Sektion sowie fuer die "Letzte Details"-Kachel.
export const COMPLETED_AB_LAST_DETAILS_FLOW_KEYS = [
  "extraWheelsDone",
  "fahrzeugHalterDone",
  "finanzierungDone",
  // extraWheels steht in LAST_DETAILS_FLOW_KEYS bereits vorn – hier nicht
  // doppeln. fitToDrive gehoert bei completed-AB NICHT in die Letzten Details:
  // AB-Kunden haben die Frage bereits in der Zustand-Gruppe (vor den Fotos)
  // beantwortet.
  ...LAST_DETAILS_FLOW_KEYS.filter(
    (key) => key !== "extraWheelsDone" && key !== "fitToDriveDone",
  ),
];
// Diese Keys werden bei completed-AB aus vehicleInformationDone (Fotos-Gate)
// herausgenommen, damit Fotos VOR den Letzten Details kommt. serviceHistory ist
// ohnehin nicht Teil von VEHICLE_INFO_REQUIRED_KEYS_AB -> hier ausgenommen.
const COMPLETED_AB_DEFERRED_INFO_KEYS =
  COMPLETED_AB_LAST_DETAILS_FLOW_KEYS.filter(
    (key) => key !== "serviceHistoryDone",
  );
const NON_AB_DEFERRED_DETAILS_KEYS = [
  "standortDone",
  "telefonnummerDone",
  "finUpdatedDone",
];

const VEHICLE_INFO_GROUP_DETAILS_BASE_KEYS = [
  "fahrzeugHalterDone",
  "finanzierungDone",
  "standortDone",
  "finUpdatedDone",
  "telefonnummerDone",
];
const VEHICLE_INFO_GROUP_DETAILS_BASE_KEYS_AB = [
  // Zweiter-Radsatz-Frage als ERSTE Letzte-Details-Frage (ersetzt das frühere
  // ExtraReifen-Modal nach den Reifen-Fotos).
  "extraWheelsDone",
  "serviceBookletDone",
  "finUpdatedDone",
];

export const getVehicleInfoGroupKeys = (vehicleData = {}, userData = null) => {
  const abFlowStarted = isAbFlowStarted(vehicleData);
  const details = abFlowStarted
    ? [...VEHICLE_INFO_GROUP_DETAILS_BASE_KEYS_AB]
    : [...VEHICLE_INFO_GROUP_DETAILS_BASE_KEYS];

  if (
    abFlowStarted &&
    vehicleData?.isCompleted === true &&
    !hasUserPhone(userData)
  ) {
    details.push("telefonnummerDone");
  }

  if (getBatteryOwner(vehicleData) === true) {
    details.push("batteryOwnershipDone");
  }

  if (isElectric(vehicleData) === true) {
    details.push("cableOwnershipDone");
  }

  if (shouldAskCableType(vehicleData)) {
    details.push("cableTypeDone");
  }

  return {
    info: abFlowStarted
      ? [...VEHICLE_INFO_GROUP_INFO_KEYS_AB]
      : [...VEHICLE_INFO_GROUP_INFO_KEYS],
    condition: abFlowStarted
      ? [...VEHICLE_INFO_GROUP_CONDITION_KEYS_AB]
      : [...VEHICLE_INFO_GROUP_CONDITION_KEYS],
    details,
  };
};

export const checkVehicleSteps = (
  userData,
  carData,
  vehicleRequirementsState = false,
  id = undefined,
  options = {},
) => {
  let completedConditions = 0;
  let totalConditions = 0;

  const isMobile = false;
  const status = makeEmptyStatus(id, isMobile);

  const vehicleData = carData || {};
  const vehicleFeatures = vehicleData?.vehicleFeatures || {};
  const vehicleCondition = vehicleData?.vehicleCondition || {};
  const ownershipInformation = vehicleData?.fahrzeug_eigentum || {};
  const carImages = vehicleData?.carImages || {};
  const priceCalculationAutomated = isPriceCalculationAutomated(vehicleData);
  const abFlowStarted = isAbFlowStarted(vehicleData);
  const shouldRequireTelefonnummerStep = !hasUserPhone(userData);

  if (!abFlowStarted) {
    AB_ONLY_KEYS.forEach((key) => {
      if (status[key]) status[key].done = true;
    });
  }

  if (!shouldRequireTelefonnummerStep && status.telefonnummerDone) {
    status.telefonnummerDone.done = true;
  }

  // If the vehicle already progressed beyond "reset", treat profile as complete.
  if (
    !options?.skipCompletedStatusShortcut &&
    isNonResetStatus(vehicleData) &&
    !(priceCalculationAutomated && vehicleData?.isCompleted !== true)
  ) {
    if (getBatteryOwner(vehicleData) === true) {
      status.batteryOwnershipDone = {
        done: true,
        path: `/${id}/profil/fahrzeug-eigentum/batterie-eigentum`,
      };
    }

    if (isElectric(vehicleData) === true) {
      status.cableOwnershipDone = {
        done: true,
        path: `/${id}/profil/fahrzeug-eigentum/ladekabel`,
      };
      status.cableTypeDone = {
        done: true,
        path: `/${id}/profil/fahrzeug-eigentum/ladekabel-typ`,
      };
    }

    Object.keys(status).forEach((key) => {
      status[key].done = true;
    });

    return {
      progress: 100,
      status,
      vehicleRequirements: true,
      vehicleInformationDone: true,
    };
  }

  const vehicleRequirements = true;

  // ─────────────────────────────────────────────
  // ✅ Damage: 10 base keys (key-aware) (+ Bild-Fallback)
  // ─────────────────────────────────────────────
  const answeredDamageSet = buildEffectiveDamageAnsweredSet(vehicleData);

  // ─────────────────────────────────────────────
  // ✅ Nachlackierung zählt als Damage
  // ─────────────────────────────────────────────
  const nachlackierungDone = isNachlackierungDone(vehicleData);

  // ✅ damagesDone = alle Hub-Fragen beantwortet + Nachlackierung
  // (nur mechanische ist keine Pflicht mehr, siehe Kopf-Kommentar)
  const damagesMet =
    HUB_DAMAGE_KEYS.every((k) => answeredDamageSet.has(k)) &&
    nachlackierungDone;

  // ─────────────────────────────────────────────
  // Dynamic steps after FIN (Battery / Cable / Cable-Type)
  // ─────────────────────────────────────────────
  const needsBatteryOwnershipStep = getBatteryOwner(vehicleData) === true;
  if (needsBatteryOwnershipStep) {
    status.batteryOwnershipDone = {
      done: false,
      path: `/${id}/profil/fahrzeug-eigentum/batterie-eigentum`,
    };
  }

  const needsCableOwnershipStep = isElectric(vehicleData) === true;
  if (needsCableOwnershipStep) {
    status.cableOwnershipDone = {
      done: false,
      path: `/${id}/profil/fahrzeug-eigentum/ladekabel`,
    };
  }

  const needsCableTypeStep = shouldAskCableType(vehicleData);
  if (needsCableTypeStep) {
    status.cableTypeDone = {
      done: false,
      path: `/${id}/profil/fahrzeug-eigentum/ladekabel-typ`,
    };
  }

  // ─────────────────────────────────────────────
  // Sonderausstattungen: optionaler Step, NICHT für Progress zählen
  // ─────────────────────────────────────────────
  const sonderAusstattungenRaw =
    vehicleFeatures?.sonderAusstattungen ?? vehicleData?.sonderAusstattungen;
  const sonderAusstattungenNone =
    vehicleFeatures?.sonderAusstattungenNone ??
    vehicleData?.sonderAusstattungenNone;

  const sonderausstattungenMet =
    (Array.isArray(sonderAusstattungenRaw) &&
      sonderAusstattungenRaw.length > 0) ||
    sonderAusstattungenNone === true;

  if (sonderausstattungenMet && status.sonderausstattungenDone) {
    status.sonderausstattungenDone.done = true;
  }

  // ─────────────────────────────────────────────
  // Conditions (Progress + Required-Step-Status)
  // ─────────────────────────────────────────────
  const conditions = [
    {
      // Telefonnummer ist im aktuellen Anmelden-Flow optional.
      // Für den Profil-Step zählt ein bestehender Account (E-Mail) als erledigt.
      met: !!userData?.email,
      key: "personalInfosDone",
    },

    { met: !!vehicleFeatures?.seatMaterial, key: "seatMaterialDone" },
    { met: !!vehicleFeatures?.keyAmount, key: "keyAmountDone" },
    { met: !!vehicleFeatures?.smoking, key: "smokedDone" },
    { met: !!vehicleFeatures?.previous_owner, key: "previousOwnerDone" },

    { met: !!vehicleFeatures?.pickerl, key: "pickerlDone" },

    {
      met:
        vehicleFeatures?.service_historie === "keineServicehistorie" ||
        vehicleFeatures?.service_historie === "nichtFällig" ||
        (vehicleFeatures?.service_historie !== "keineServicehistorie" &&
          (vehicleData?.serviceHistory || []).length > 0),
      key: "serviceHistoryDone",
    },

    { met: !!ownershipInformation?.fahrzeug_halter, key: "fahrzeugHalterDone" },
    { met: !!ownershipInformation?.finanzierung, key: "finanzierungDone" },
    { met: !!ownershipInformation?.address, key: "standortDone" },

    {
      met:
        !!carImages?.vordereFahrerecke &&
        !!carImages?.vordereBeifahrerecke &&
        !!carImages?.fahrerseiteHinten &&
        !!carImages?.beifahrerseiteHinten,
      key: "exteriorPhotosDone",
    },
    {
      // Interior fertig — geteilte, backend-deckungsgleiche Regel (unabhaengige
      // Ruecksitzbank-/Kofferraum-Exemption). Frueher exemptierte EIN Flag
      // BEIDE Optional-Fotos, was von Backend/Worker abwich.
      met: isInteriorComplete(carImages, vehicleFeatures),
      key: "interiorPhotosDone",
    },
    {
      met:
        !!carImages?.vordereFahrerfelge &&
        !!carImages?.vordereBeifahrerfelge &&
        !!carImages?.hintereFahrerfelge &&
        !!carImages?.hintereBeifahrerfelge,
      key: "rimsPhotosDone",
    },
    {
      met:
        !!carImages?.vordererFahrerreifen &&
        !!carImages?.vordererBeifahrerreifen &&
        !!carImages?.hintererFahrerreifen &&
        !!carImages?.hintererBeifahrerreifen,
      key: "wheelsPhotosDone",
    },

    // ✅ FIXED: now matches your Firestore structure
    { met: damagesMet, key: "damagesDone" },

    {
      met:
        !vehicleData?.manuallySet ||
        !!vehicleData?.finUpdated ||
        !!vehicleData?.finAlreadySet ||
        hasFinPresent(vehicleData),
      key: "finUpdatedDone",
    },
  ];

  if (shouldRequireTelefonnummerStep) {
    conditions.push({ met: hasUserPhone(userData), key: "telefonnummerDone" });
  }

  // Radsatz + Fahrtuechtig sind die ersten beiden Letzte-Details-Fragen in
  // ALLEN Flows – ihre Erledigung wird daher fuer alle Flows aus den echten
  // gespeicherten Antworten berechnet (frueher AB-only + non-AB auto-done;
  // das liess den Profil-Weiter-Button die beiden Fragen ueberspringen und
  // die Letzte-Details-Kachel zu frueh als fertig zaehlen).
  const extraReifenValue = vehicleFeatures?.extraReifen;
  const hasExtraReifen =
    extraReifenValue !== undefined &&
    extraReifenValue !== null &&
    String(extraReifenValue).trim() !== "";
  conditions.push(
    {
      met: typeof vehicleFeatures?.extraWheels === "boolean" || hasExtraReifen,
      key: "extraWheelsDone",
    },
    {
      met:
        typeof vehicleCondition?.fitToDrive === "boolean" ||
        isAnsweredDamageValue(
          "mechanische_oder_elektrische",
          vehicleData?.carDamageState?.mechanische_oder_elektrische,
        ),
      key: "fitToDriveDone",
    },
  );

  if (abFlowStarted) {
    // aussenfarbe deaktiviert: KEINE Progress-Bedingung mehr (sonst bliebe der
    // Fortschritt < 100 %, weil die Frage nie beantwortet wird).
    conditions.push(
      {
        met: Array.isArray(vehicleCondition?.wear_and_tear)
          ? vehicleCondition.wear_and_tear.length > 0
          : !!vehicleCondition?.wear_and_tear,
        key: "wearAndTearDone",
      },
      {
        met: isServiceBookletQuestionDone(vehicleCondition),
        key: "serviceBookletDone",
      },
    );
  }

  if (needsBatteryOwnershipStep) {
    conditions.push({
      met: !!ownershipInformation?.battery_ownership,
      key: "batteryOwnershipDone",
    });
  }

  if (needsCableOwnershipStep) {
    conditions.push({
      met: !!ownershipInformation?.cable_ownership,
      key: "cableOwnershipDone",
    });
  }

  if (needsCableTypeStep) {
    const cableTypeRaw =
      ownershipInformation?.cable_typ ?? ownershipInformation?.cable_type;

    const hasCableType = Array.isArray(cableTypeRaw)
      ? cableTypeRaw.length > 0
      : !!cableTypeRaw;

    conditions.push({
      met: hasCableType,
      key: "cableTypeDone",
    });
  }

  // count + set status
  conditions.forEach(({ met, key }) => {
    totalConditions++;
    if (met) {
      completedConditions++;
      if (key && status[key]) status[key].done = true;
    }
  });

  // ─────────────────────────────────────────────
  // VehicleInformationDone: nur REQUIRED + dynamische Steps
  // ─────────────────────────────────────────────
  const deferPostPriceDetails =
    abFlowStarted && vehicleData?.isCompleted !== true;
  // Completed-AB: Post-Price (Letzte Details) NICHT in vehicleInformationDone
  // zaehlen -> Fotos entsperrt nach Fahrzeugdetails, Letzte Details kommen danach.
  const deferCompletedAbLastDetails =
    abFlowStarted && vehicleData?.isCompleted === true;
  const deferredInfoKeys = new Set([
    ...(deferPostPriceDetails ? POST_PRICE_DETAILS_KEYS : []),
    ...(deferCompletedAbLastDetails ? COMPLETED_AB_DEFERRED_INFO_KEYS : []),
    ...(!abFlowStarted ? NON_AB_DEFERRED_DETAILS_KEYS : []),
  ]);
  const baseInfoKeys = (
    abFlowStarted ? VEHICLE_INFO_REQUIRED_KEYS_AB : VEHICLE_INFO_REQUIRED_KEYS
  ).filter((k) => !deferredInfoKeys.has(k));

  const requiredInfoKeys = [
    ...baseInfoKeys,
    ...(needsBatteryOwnershipStep ? ["batteryOwnershipDone"] : []),
    ...(needsCableOwnershipStep ? ["cableOwnershipDone"] : []),
    ...(needsCableTypeStep ? ["cableTypeDone"] : []),
  ];

  const vehicleInformationDone = requiredInfoKeys.every((k) => status[k]?.done);
  let progress = Math.round((completedConditions / totalConditions) * 100);

  if (abFlowStarted && vehicleData?.isCompleted !== true) {
    const flowKeys = getVehicleInfoFlowKeys(vehicleData, userData);
    const infoTotal = flowKeys.length;
    const infoDone = flowKeys.filter((k) => status[k]?.done).length;
    progress = infoTotal ? Math.round((infoDone / infoTotal) * 100) : 0;
    if (status?.personalInfosDone?.done) {
      progress = Math.max(progress, 5);
    }
  }

  return { progress, status, vehicleRequirements, vehicleInformationDone };
};

const isFinalConfirmationDone = (carData = {}) => {
  const c = carData?.confirmInformation || {};
  return (
    c.personalInformation === true &&
    c.vehicleInformation === true &&
    c.photos === true &&
    c.damages === true &&
    (c.service === true || c.serviceHistory === true)
  );
};

const FINAL_BASE_REQUIRED_STEP_KEYS = [
  "seatMaterialDone",
  "keyAmountDone",
  "smokedDone",
  "previousOwnerDone",
  "pickerlDone",
  "serviceHistoryDone",
  "fahrzeugHalterDone",
  "finanzierungDone",
  // Radsatz + Fahrtuechtig sind Letzte-Details-Pflicht in ALLEN Flows
  // (frueher nur FINAL_AB): sonst kaeme man per Deep-Link auf
  // kaufpreis-bestaetigen/naechste-schritte an den Fragen vorbei.
  "extraWheelsDone",
  "fitToDriveDone",
  "standortDone",
  "exteriorPhotosDone",
  "rimsPhotosDone",
  "wheelsPhotosDone",
  "interiorPhotosDone",
  "damagesDone",
  "finUpdatedDone",
  "telefonnummerDone",
];

const FINAL_AB_REQUIRED_STEP_KEYS = [
  // aussenfarbe deaktiviert (nicht mehr Abschluss-Pflicht).
  // extraWheels + fitToDrive stehen in FINAL_BASE (Pflicht in allen Flows).
  "wearAndTearDone",
  "serviceBookletDone",
];

const FINAL_DYNAMIC_STEP_KEYS = [
  "batteryOwnershipDone",
  "cableOwnershipDone",
  "cableTypeDone",
];

const getFinalRequiredStepKeys = (status = {}, carData = {}) => {
  const keys = [
    ...FINAL_BASE_REQUIRED_STEP_KEYS,
    ...(isAbFlowStarted(carData) ? FINAL_AB_REQUIRED_STEP_KEYS : []),
    ...FINAL_DYNAMIC_STEP_KEYS.filter((key) => status[key]),
  ];

  return keys.filter((key) => status[key]);
};

// Bild-/Schaden-Erfassungs-Steps. Werden beim Re-Listing (Admin loescht via
// DELETE /car-images/delete Fotos/Schaden-Positionen; status bleibt "finished")
// wieder "nicht erledigt" und treiben so den Re-Erfassungs-Flow. Reihenfolge +
// Semantik = Production (FINAL_BASE ab Foto-Start).
//
// INVARIANTE (bewusst = Production, NICHT strenger): Diese Keys spiegeln exakt
// die entsprechenden Keys in getFinalProfileStepGuard. Insbesondere ist
// damagesDone antwort-basiert (carDamageState) mit Schadenbild-Fallback -- ein
// geloeschtes Schadenbild bei erhaltener Antwort loest hier KEINE Re-Erfassung
// aus, exakt wie im heutigen Production-Guard. Die 4 Haupt-Fotos haengen an
// carImages und werden beim Loeschen zuverlaessig offen. NICHT dabei:
// extraWheels/fitToDrive/uebrige Frage-Steps -- die sind der zu fixende Bug.
const OFFER_RELISTING_STEP_KEYS = [
  "exteriorPhotosDone",
  "rimsPhotosDone",
  "wheelsPhotosDone",
  "interiorPhotosDone",
  "damagesDone",
];

const hasUsableOfferPrice = (raw) => {
  if (raw === null || raw === undefined) return false;
  const n =
    typeof raw === "number"
      ? raw
      : parseInt(String(raw).replace(/[^\d]/g, ""), 10);
  return Number.isFinite(n) && n > 0;
};

/**
 * Angebots-Flow-Guard fuer bereits auktionierte, FERTIGE Fahrzeuge mit Angebot.
 * Solche Fahrzeuge sollen ihr Angebot sehen und NICHT zurueck zu (evtl. neu
 * hinzugekommenen) Profil-Fragen geschickt werden. Einzige Ausnahme Re-Listing:
 * Admin loescht Fotos/Schadenbilder -> die Bild-/Schaden-Erfassungs-Steps werden
 * wieder offen -> Kunde erfasst neu (spiegelt Production). Deshalb gaten wir hier
 * NUR diese Erfassungs-Steps, nicht die uebrigen Frage-/Bestaetigungs-Steps
 * (extraWheels/fitToDrive etc. -- der zu fixende Bug).
 *
 * getFinalProfileStepGuard bleibt bewusst unveraendert (weitere Nutzer:
 * Kaufpreis-, Richtpreis-, Status-Flow).
 *
 * @returns {{applies:boolean, canAccessOffer:boolean, redirectPath:string|null}}
 *   applies=false -> kein Angebots-Fahrzeug; Caller faellt auf
 *   getFinalProfileStepGuard zurueck.
 */
export const getOfferFlowGuard = ({ userData, carData, id }) => {
  const auction = carData?.auction || {};
  const hasOffer = hasUsableOfferPrice(auction.finalPrice);
  const isFinished = carData?.status === "finished";

  // Greift nur fuer fertige Auktions-Fahrzeuge mit Angebot.
  if (!isFinished || !hasOffer) {
    return { applies: false, canAccessOffer: false, redirectPath: null };
  }

  // Terminale Ziele zuerst (nie zurueck zu den Fragen).
  if (auction.offerAcceptedDate != null) {
    return {
      applies: true,
      canAccessOffer: false,
      redirectPath: `/${id}/profil/fahrzeug-verkauft`,
    };
  }
  if (auction.sellStatus === "cancelled") {
    return {
      applies: true,
      canAccessOffer: false,
      redirectPath: `/${id}/profil/verkauf-storniert`,
    };
  }

  // Re-Listing: nur die Bild-/Schaden-Erfassungs-Steps gaten (wie Production ab
  // Foto-Start). Fehlt eine Sektion (Admin hat Bilder/Schadenbilder geloescht)
  // -> zum Erfassungs-Flow; sonst -> Angebot anzeigen (auch bei abgelaufenem
  // Angebot; die Angebots-Seite zeigt dann den Ablauf-Zustand).
  const { status } = checkVehicleSteps(userData, carData, false, id, {
    skipCompletedStatusShortcut: true,
  });
  const missingCaptureKey = OFFER_RELISTING_STEP_KEYS.find(
    (key) => status[key] && status[key].done !== true,
  );
  if (missingCaptureKey) {
    return {
      applies: true,
      canAccessOffer: false,
      redirectPath: status[missingCaptureKey]?.path || `/${id}/profil`,
    };
  }

  return { applies: true, canAccessOffer: true, redirectPath: null };
};

export const getFinalProfileStepGuard = ({
  userData,
  carData,
  id,
  vehicleRequirementsState = false,
}) => {
  const { status } = checkVehicleSteps(
    userData,
    carData,
    vehicleRequirementsState,
    id,
    { skipCompletedStatusShortcut: true },
  );

  const requiredStepKeys = getFinalRequiredStepKeys(status, carData);
  const firstMissingRequiredKey = requiredStepKeys.find(
    (key) => status[key]?.done !== true,
  );
  const questionStepsDone = !firstMissingRequiredKey;
  const confirmationDone = isFinalConfirmationDone(carData);

  if (!questionStepsDone) {
    const firstIncompleteStep = status[firstMissingRequiredKey];

    return {
      canAccessFinalStep: false,
      questionStepsDone,
      confirmationDone,
      redirectPath: firstIncompleteStep?.path || `/${id}/profil`,
      missingStepKey: firstMissingRequiredKey,
      status,
    };
  }

  if (!confirmationDone) {
    return {
      canAccessFinalStep: false,
      questionStepsDone,
      confirmationDone,
      redirectPath: `/${id}/profil/profil-bestaetigen`,
      status,
    };
  }

  return {
    canAccessFinalStep: true,
    questionStepsDone,
    confirmationDone,
    redirectPath: null,
    status,
  };
};

// shallow merge for the parts we check so you can pass a "just set" patch
const mergeCarDataForSteps = (carData = {}, patch = {}) => ({
  ...carData,
  ...patch,
  vehicleFeatures: {
    ...(carData?.vehicleFeatures || {}),
    ...(patch?.vehicleFeatures || {}),
  },
  vehicleCondition: {
    ...(carData?.vehicleCondition || {}),
    ...(patch?.vehicleCondition || {}),
  },
  fahrzeug_eigentum: {
    ...(carData?.fahrzeug_eigentum || {}),
    ...(patch?.fahrzeug_eigentum || {}),
  },
  carImages: {
    ...(carData?.carImages || {}),
    ...(patch?.carImages || {}),
  },
  carDamageState: {
    ...(carData?.carDamageState || {}),
    ...(patch?.carDamageState || {}),
  },
  carDamageSize: {
    ...(carData?.carDamageSize || {}),
    ...(patch?.carDamageSize || {}),
  },
  // carDamages einbeziehen, damit lokale Bild-Patches sofort wirken
  carDamages: {
    ...(carData?.carDamages || {}),
    ...(patch?.carDamages || {}),
  },
  serviceHistory:
    patch?.serviceHistory !== undefined
      ? patch.serviceHistory
      : carData?.serviceHistory,
});

/**
 * Get the next route in the *vehicle information* flow (up to FIN + dynamic).
 * If everything is done, returns the summary route.
 */
export const getNextVehicleInfoPath = ({
  userData,
  carData,
  id,
  vehicleRequirementsState = false,
  carDataPatch,
}) => {
  const merged = carDataPatch
    ? mergeCarDataForSteps(carData, carDataPatch)
    : carData;

  const { status, vehicleInformationDone } = checkVehicleSteps(
    userData,
    merged,
    vehicleRequirementsState,
    id,
  );

  if (areAllQuestionStepsDone(status)) {
    return `/${id}/profil`;
  }

  if (vehicleInformationDone) {
    const priceCalculationAutomated = isPriceCalculationAutomated(merged);
    const abFlowStarted = isAbFlowStarted(merged);
    if (priceCalculationAutomated) {
      if (abFlowStarted) {
        return hasStartPrice(merged) ? `/${id}/profil` : `/status/${id}`;
      }
      return `/${id}/profil`;
    }
    return `/${id}/profil`;
  }

  const priceCalculationAutomated = isPriceCalculationAutomated(merged);
  const abFlowStarted = isAbFlowStarted(merged);
  const deferPostPriceDetails =
    abFlowStarted && merged?.isCompleted !== true;
  const deferCompletedAbLastDetails =
    abFlowStarted && merged?.isCompleted === true;
  const deferredInfoKeys = new Set([
    ...(deferPostPriceDetails ? POST_PRICE_DETAILS_KEYS : []),
    ...(deferCompletedAbLastDetails ? COMPLETED_AB_DEFERRED_INFO_KEYS : []),
    ...(!abFlowStarted ? NON_AB_DEFERRED_DETAILS_KEYS : []),
  ]);
  const baseOrder = [
    ...VEHICLE_INFO_OPTIONAL_KEYS,
    ...(abFlowStarted ? VEHICLE_INFO_REQUIRED_KEYS_AB : VEHICLE_INFO_REQUIRED_KEYS)
      .filter((k) => !deferredInfoKeys.has(k)),
  ];

  const dynamicKeys = [
    ...(getBatteryOwner(merged) === true ? ["batteryOwnershipDone"] : []),
    ...(isElectric(merged) === true ? ["cableOwnershipDone"] : []),
    ...(isElectric(merged) === true ? ["cableTypeDone"] : []),
  ];

  const keys = [...baseOrder, ...dynamicKeys];

  for (const key of keys) {
    const step = status[key];
    if (step && !step.done) return step.path;
  }

  if (priceCalculationAutomated) {
    if (abFlowStarted) {
      return hasStartPrice(merged) ? `/${id}/profil` : `/status/${id}`;
    }
    return `/${id}/profil`;
  }
  return `/${id}/profil`;
};

// Build the full ordered vehicle-info flow (info -> condition -> details)
export const getVehicleInfoFlowKeys = (vehicleData = {}, userData = null) => {
  const abFlowStarted = isAbFlowStarted(vehicleData);
  const groups = getVehicleInfoGroupKeys(vehicleData, userData);
  const info = groups?.info || [];
  const condition = groups?.condition || [];
  const details = groups?.details || [];
  const dynamicKeys = [
    ...(getBatteryOwner(vehicleData) === true ? ["batteryOwnershipDone"] : []),
    ...(isElectric(vehicleData) === true ? ["cableOwnershipDone"] : []),
    ...(shouldAskCableType(vehicleData) ? ["cableTypeDone"] : []),
  ];

  if (!abFlowStarted) {
    const baseFlow = [
      ...VEHICLE_INFO_OPTIONAL_KEYS,
      ...VEHICLE_INFO_REQUIRED_KEYS.filter(
        (k) => !NON_AB_DEFERRED_DETAILS_KEYS.includes(k),
      ),
    ];
    return [...baseFlow, ...dynamicKeys];
  }

  const specialKeys = [
    "batteryOwnershipDone",
    "cableOwnershipDone",
    "cableTypeDone",
  ].filter((key) => details.includes(key));
  const detailsWithoutSpecial = details.filter((key) => !specialKeys.includes(key));

  if (vehicleData?.isCompleted === true) {
    // Completed-AB: der Vehicle-Info-(Fahrzeugdetails-)Flow enthaelt NUR die
    // Schritte VOR Fotos: Info-/AB-Gruppe (inkl. Elektro battery/cable/cableType
    // in specialKeys) -> Zustand -> serviceBooklet. Die Post-Price-Schritte
    // (fahrzeugHalter, finanzierung, standort, telefon, finUpdated, serviceHistory)
    // sind "Letzte Details" und kommen NACH Fotos – sie werden hier ueber
    // COMPLETED_AB_DEFERRED_INFO_KEYS herausgefiltert und stattdessen ueber
    // COMPLETED_AB_LAST_DETAILS_FLOW_KEYS (getNext/getPrev + Letzte-Details-Kachel)
    // gefahren. So entspricht der Flow der Sektions-Struktur (activePostPriceStep:
    // photos -> details), und Fotos kommt vor den Letzten Details. Das vermeidet
    // auch den frueheren Loop (NUR-POST_PRICE -> Fallback auf keys[0]).
    const completedAbVehicleInfoDetails = detailsWithoutSpecial.filter(
      (key) => !COMPLETED_AB_DEFERRED_INFO_KEYS.includes(key),
    );
    return [
      ...info,
      ...specialKeys,
      ...condition,
      ...completedAbVehicleInfoDetails,
    ];
  }

  return [...info, ...specialKeys, ...condition, ...detailsWithoutSpecial];
};

/**
 * Entry path for the vehicle-info flow:
 * - AB flow: if all or none done -> first question, else first missing
 * - non-AB: fallback to getNextVehicleInfoPath
 */
export const getVehicleInfoEntryPath = ({
  userData,
  carData,
  id,
  vehicleRequirementsState = false,
  carDataPatch,
}) => {
  const merged = carDataPatch
    ? mergeCarDataForSteps(carData, carDataPatch)
    : carData;

  const abFlowStarted = isAbFlowStarted(merged);

  if (!abFlowStarted) {
    return getNextVehicleInfoPath({
      userData,
      carData: merged,
      id,
      vehicleRequirementsState,
    });
  }

  const { status } = checkVehicleSteps(
    userData,
    merged,
    vehicleRequirementsState,
    id,
  );

  const keys = getVehicleInfoFlowKeys(merged, userData);
  if (!keys.length) return `/${id}/profil`;

  const allDone = keys.every((k) => status[k]?.done === true);
  const noneDone = keys.every((k) => status[k]?.done !== true);

  if (allDone || noneDone) {
    const firstKey = keys[0];
    if (firstKey && status[firstKey]) return status[firstKey].path;
    return `/${id}/profil`;
  }

  for (const key of keys) {
    const step = status[key];
    if (step && !step.done) return step.path;
  }

  return `/${id}/profil`;
};

/**
 * Sequential next question in the vehicle-info flow:
 * - AB and non-AB: move to the next not-yet-done key in order (skips already
 *   answered steps; getPrev stays sequential for backward review)
 */
export const getNextVehicleInfoSequentialPath = ({
  userData,
  carData,
  id,
  currentKey,
  vehicleRequirementsState = false,
  carDataPatch,
}) => {
  const merged = carDataPatch
    ? mergeCarDataForSteps(carData, carDataPatch)
    : carData;

  const { status } = checkVehicleSteps(
    userData,
    merged,
    vehicleRequirementsState,
    id,
  );

  // Von der Bestätigungsseite per Edit-Stift geoeffnet (?returnTo=…):
  // nach dem Speichern direkt dorthin zurueck statt die restliche
  // Frage-Sequenz durchzublaettern. ABER: Wenn die geaenderte Antwort einen
  // Pflicht-Folgestep aufmacht (z.B. Ladekabel "habe Kabel" -> Ladekabel-Typ),
  // zuerst dorthin (returnTo bleibt erhalten), sonst fehlen abhaengige Daten.
  // Ohne returnTo (normaler Flow) unveraendert.
  const confirmationReturnTo = getConfirmationReturnTo(id);
  if (confirmationReturnTo) {
    const requiredKeys = getFinalRequiredStepKeys(status, merged);
    const firstMissingKey = requiredKeys.find(
      (key) => status[key]?.done !== true,
    );
    const firstMissingPath = firstMissingKey
      ? status[firstMissingKey]?.path
      : null;

    if (!firstMissingPath) return confirmationReturnTo;

    const joiner = firstMissingPath.includes("?") ? "&" : "?";
    return `${firstMissingPath}${joiner}returnTo=${encodeURIComponent(
      confirmationReturnTo,
    )}`;
  }

  const abFlowStarted = isAbFlowStarted(merged);
  // Completed-AB "Letzte Details" (NACH Fotos): fahrzeugHalter -> finanzierung ->
  // standort -> telefon -> fin -> service-historie. Faengt VOR dem LAST_DETAILS-
  // Block, weil dessen Keys (standort/telefon/fin/service) eine Teilmenge sind.
  const completedAbLastDetailsFlow =
    abFlowStarted && merged?.isCompleted === true
      ? COMPLETED_AB_LAST_DETAILS_FLOW_KEYS
      : null;

  if (currentKey && completedAbLastDetailsFlow?.includes(currentKey)) {
    const currentIndex = completedAbLastDetailsFlow.indexOf(currentKey);
    for (let i = currentIndex + 1; i < completedAbLastDetailsFlow.length; i++) {
      const nextKey = completedAbLastDetailsFlow[i];
      const nextStep = status?.[nextKey];
      if (!nextStep) continue;
      if (nextKey === "telefonnummerDone" && nextStep.done === true) continue;
      return nextStep.path;
    }
    return `/${id}/profil`;
  }

  const keys = getVehicleInfoFlowKeys(merged, userData);

  // NUR fuer Letzte-Details-Keys, die NICHT Teil der regulaeren Flow-Keys
  // sind (Spiegel von getPrev): extraWheels liegt im AB-noch-nicht-fertig-Flow
  // in den Flow-Keys (erste Details-Gruppen-Frage) – dort uebernimmt der
  // Flow-Walk unten (extraWheels -> serviceheft), NICHT diese Teilsequenz.
  if (
    currentKey &&
    LAST_DETAILS_FLOW_KEYS.includes(currentKey) &&
    keys.indexOf(currentKey) < 0
  ) {
    const currentIndex = LAST_DETAILS_FLOW_KEYS.indexOf(currentKey);
    if (currentIndex >= 0) {
      for (let i = currentIndex + 1; i < LAST_DETAILS_FLOW_KEYS.length; i++) {
        const nextKey = LAST_DETAILS_FLOW_KEYS[i];
        const nextStep = status?.[nextKey];
        if (!nextStep) continue;

        // Telefonnummer nur überspringen, wenn sie bereits vorhanden ist.
        if (nextKey === "telefonnummerDone" && nextStep.done === true) {
          continue;
        }

        return nextStep.path;
      }
    }

    return `/${id}/profil`;
  }

  if (!keys.length) return `/${id}/profil`;

  const startIndex = currentKey ? keys.indexOf(currentKey) : -1;
  // Wurde ein currentKey uebergeben, der NICHT im Flow liegt (indexOf === -1),
  // NICHT auf keys[0] zurueckfallen: genau das liess die Navigation nach dem
  // Speichern eines (frueher flow-fremden) Schritts auf den ersten Key springen
  // (z.B. fahrzeugHalter). Es gibt keinen sinnvollen "naechsten" -> /profil.
  if (currentKey && startIndex < 0) {
    return `/${id}/profil`;
  }

  // NUR im AB-Flow: den naechsten NOCH OFFENEN Schritt liefern (bereits
  // beantwortete ueberspringen). Verhindert den Re-Walk durch erledigte Fragen bis
  // zur AB-Sub-Gruppen-Grenze (extra-raeder/schaeden -> /profil), wenn der Einstieg
  // an einer offenen optionalen Frage (sonderausstattungen) erfolgt und die
  // Folgefragen schon erledigt sind.
  // Bei non-AB NICHT ueberspringen: dort liegt fahrzeughalter (Index 5) regulaer in
  // den Flow-Keys und es gibt keine Sub-Gruppen-Grenze -> sequentiell wie main.
  // Sonst wuerde getNext(sonderausstattungen) ueber erledigte Info-Fragen direkt
  // auf fahrzeughalter springen (= "2. Frage = Eingetragener Halter"-Bug).
  // getPrev bleibt sequentiell; die Letzte-Details-Spezialbloecke oben unberuehrt.
  for (let i = startIndex + 1; i < keys.length; i++) {
    const key = keys[i];
    const step = status[key];
    if (!step || (abFlowStarted && step.done === true)) continue;
    return step.path;
  }

  // End of flow: always return to profil in AB flow
  return `/${id}/profil`;
};

/**
 * Previous question in the vehicle-info flow (AB only).
 */
export const getPrevVehicleInfoSequentialPath = ({
  userData,
  carData,
  id,
  currentKey,
  vehicleRequirementsState = false,
  carDataPatch,
}) => {
  const merged = carDataPatch
    ? mergeCarDataForSteps(carData, carDataPatch)
    : carData;

  const { status } = checkVehicleSteps(
    userData,
    merged,
    vehicleRequirementsState,
    id,
  );

  const keys = getVehicleInfoFlowKeys(merged, userData);
  const flowIndex = currentKey ? keys.indexOf(currentKey) : -1;

  // Completed-AB "Letzte Details" rueckwaerts: exakte Umkehrung von getNext ueber
  // COMPLETED_AB_LAST_DETAILS_FLOW_KEYS (fahrzeugHalter..service-historie). Diese
  // Keys liegen bei completed-AB NICHT in den Flow-Keys (flowIndex < 0).
  const completedAbLastDetailsFlow =
    isAbFlowStarted(merged) && merged?.isCompleted === true
      ? COMPLETED_AB_LAST_DETAILS_FLOW_KEYS
      : null;

  if (currentKey && completedAbLastDetailsFlow?.includes(currentKey)) {
    const ldIndex = completedAbLastDetailsFlow.indexOf(currentKey);
    if (ldIndex > 0) {
      for (let i = ldIndex - 1; i >= 0; i -= 1) {
        const prevKey = completedAbLastDetailsFlow[i];
        const prevStep = status?.[prevKey];
        if (!prevStep) continue;
        if (prevKey === "telefonnummerDone" && prevStep.done === true) continue;
        return prevStep.path;
      }
    }
    return `/${id}/profil`;
  }

  // Letzte-Details-Teilsequenz (standort→telefon→fin→service): exakte Umkehrung
  // der Vorwaerts-Order. NUR fuer Letzte-Details-Keys, die NICHT Teil der
  // regulaeren Flow-Keys sind – also die angehaengte Teilsequenz im
  // AB-noch-nicht-fertig-Flow (standort/telefon/service). Sind sie Teil der
  // Flow-Keys (Post-Price: standort/telefon stehen in POST_PRICE_DETAILS_KEYS),
  // uebernimmt der Flow-Walk unten – das spiegelt getNext exakt.
  if (
    currentKey &&
    LAST_DETAILS_FLOW_KEYS.includes(currentKey) &&
    flowIndex < 0
  ) {
    const ldIndex = LAST_DETAILS_FLOW_KEYS.indexOf(currentKey);
    if (ldIndex > 0) {
      for (let i = ldIndex - 1; i >= 0; i -= 1) {
        const prevKey = LAST_DETAILS_FLOW_KEYS[i];
        const prevStep = status?.[prevKey];
        if (!prevStep) continue;
        // Telefonnummer ueberspringen, wenn bereits verifiziert (Spiegel von Weiter).
        if (prevKey === "telefonnummerDone" && prevStep.done === true) {
          continue;
        }
        return prevStep.path;
      }
    }
    return `/${id}/profil`;
  }

  if (!keys.length) return `/${id}/profil`;
  for (let i = flowIndex - 1; i >= 0; i -= 1) {
    const prevKey = keys[i];
    if (status[prevKey]) return status[prevKey].path;
  }

  return `/${id}/profil`;
};

/**
 * Get the next route within a specific vehicle-info group.
 * Groups: "info" | "condition" | "details"
 * - For priceCalculationAutomated === false, falls back to getNextVehicleInfoPath.
 * - When the group is complete, returns the profile route.
 */
export const getNextVehicleInfoGroupPath = ({
  group,
  userData,
  carData,
  id,
  vehicleRequirementsState = false,
  carDataPatch,
  startAtFirst = false,
  forceOrder = false,
  currentKey,
}) => {
  const merged = carDataPatch
    ? mergeCarDataForSteps(carData, carDataPatch)
    : carData;

  const abFlowStarted = isAbFlowStarted(merged);

  if (!abFlowStarted) {
    return getNextVehicleInfoPath({
      userData,
      carData: merged,
      id,
      vehicleRequirementsState,
    });
  }

  const groups = getVehicleInfoGroupKeys(merged, userData);
  const keys = groups?.[group] || [];

  const { status, vehicleInformationDone } = checkVehicleSteps(
    userData,
    merged,
    vehicleRequirementsState,
    id,
  );

  if (forceOrder) {
    const currentIndex = currentKey ? keys.indexOf(currentKey) : -1;
    const nextKey =
      currentIndex >= 0 ? keys[currentIndex + 1] : keys[0];
    if (nextKey && status[nextKey]) return status[nextKey].path;
    return `/${id}/profil`;
  }

  for (const key of keys) {
    const step = status[key];
    if (step && !step.done) return step.path;
  }

  if (startAtFirst) {
    const firstKey = keys[0];
    if (firstKey && status[firstKey]) return status[firstKey].path;
    return `/${id}/profil`;
  }

  if (vehicleInformationDone) {
    if (abFlowStarted) {
      return `/${id}/profil`;
    }
    return `/${id}/profil`;
  }

  return `/${id}/profil`;
};

/**
 * Get the next route in the *photo flow*:
 * - Außenfotos
 * - Felgen
 * - Reifen
 * - Innenfotos
 * - Schäden
 *
 * (Der Kunde steht nach den Außenfotos bereits am Fahrzeug – daher erst
 * Felgen + Reifenprofil, dann erst der Innenraum.)
 *
 * If everything is done, falls back to the summary route.
 */
export const getNextPhotosPath = ({
  userData,
  carData,
  id,
  vehicleRequirementsState = false,
  carDataPatch,
}) => {
  const merged = carDataPatch
    ? mergeCarDataForSteps(carData, carDataPatch)
    : carData;

  const { status } = checkVehicleSteps(
    userData,
    merged,
    vehicleRequirementsState,
    id,
  );

  const photoFlowKeys = [
    "exteriorPhotosDone",
    "rimsPhotosDone",
    "wheelsPhotosDone",
    "interiorPhotosDone",
    "damagesDone",
  ];
  const photoFlowPathByKey = {
    exteriorPhotosDone: `/${id}/profil/fotos/fotos-schiessen/exterior`,
    interiorPhotosDone: `/${id}/profil/fotos/fotos-schiessen/interior`,
    rimsPhotosDone: `/${id}/profil/fotos/fotos-schiessen/felgen`,
    wheelsPhotosDone: `/${id}/profil/fotos/fotos-schiessen/reifen`,
    damagesDone: `/${id}/profil/fotos/fotos-schiessen/schaeden`,
  };

  for (const key of photoFlowKeys) {
    const step = status[key];
    if (step && !step.done) {
      return photoFlowPathByKey[key] || step.path;
    }
  }

  return `/${id}/profil`;
};

/**
 * Wie getNextPhotosPath, aber "gefuehrt": schiebt zwischen fertigen
 * Foto-Steps die noch offenen Zwischenfragen ein – exakt die Ziele, die der
 * Weiter-Button der jeweiligen Foto-Seite ansteuern wuerde:
 *   exterior  -> exterior-fertig-Slides (fuehren zu Karosserieschaeden ->
 *                Aussenschaeden-Overview)
 *   felgen    -> felgen-schaeden ("Sind die Felgen beschaedigt?")
 *   reifen    -> reifen-schaeden ("Reifen abgenutzt oder beschaedigt?")
 *   interior  -> warnleuchten-check
 * Genutzt vom Weiter der Foto-Uebersicht: Wer nach den Fotos per Zurueck zur
 * Uebersicht geht, wuerde die Zwischenfrage sonst ueberspringen und sie erst
 * gebuendelt im Schaeden-Hub sehen. Die "Frage offen"-Praedikate sind 1:1
 * von den Seiten uebernommen (Exterior: Schaden-Fotos vorhanden ODER alle 5
 * Karosserie-Kategorien beantwortet; Checks/Warnleuchten: Key vorhanden –
 * gleiche Semantik wie die Hub-Kacheln).
 */
export const getNextGuidedPhotosPath = ({
  userData,
  carData,
  id,
  vehicleRequirementsState = false,
  carDataPatch,
}) => {
  const merged = carDataPatch
    ? mergeCarDataForSteps(carData, carDataPatch)
    : carData;

  const { status } = checkVehicleSteps(
    userData,
    merged,
    vehicleRequirementsState,
    id,
  );

  const ds = merged?.carDamageState || {};
  const base = `/${id}/profil/fotos/fotos-schiessen`;

  // 1) Exterior-Fotos + Karosserieschaeden-Kette
  if (!status.exteriorPhotosDone?.done) return `${base}/exterior`;
  // Die Karosserie-Einheit ist erst KOMPLETT, wenn alle 5 Kategorien
  // beantwortet sind. Karosserie-Schadenfotos allein reichen nicht: dann zur
  // Außenschäden-Overview (deren Weiter füllt die restlichen Kategorien als
  // "keine" auf) statt sie zu überspringen. Nicht-Karosserie-Fotos aus
  // Bestandsdaten (z.B. reifen_1) zählen gar nicht.
  const aussenKeysAnswered = [
    "kratzer",
    "lackprobleme",
    "windschutzscheibe",
    "dellen",
    "anbauteile",
  ].every((k) => ds[k] != null);
  if (!aussenKeysAnswered) {
    const hasKarosserieDamagePhotos =
      !!merged?.carDamages &&
      Object.keys(merged.carDamages).some((k) =>
        /^(kratzer|lackprobleme|windschutzscheibe|dellen|anbauteile)_\d+$/.test(
          k,
        ),
      );
    return hasKarosserieDamagePhotos
      ? `${base}/aussenschaeden`
      : `${base}/exterior-fertig`;
  }

  // 2) Felgen-Fotos + Felgen-Check
  if (!status.rimsPhotosDone?.done) return `${base}/felgen`;
  if (ds.felgen === undefined) return `${base}/felgen-schaeden`;

  // 3) Reifen-Fotos + Reifen-Check
  if (!status.wheelsPhotosDone?.done) return `${base}/reifen`;
  if (ds.reifen === undefined) return `${base}/reifen-schaeden`;

  // 4) Interior-Fotos + Warnleuchten-Check
  if (!status.interiorPhotosDone?.done) return `${base}/interior`;
  if (ds.warnleuchten === undefined) return `${base}/warnleuchten-check`;

  // 5) Restliche Schaden-Fragen (Nachlackierung/Zusatz) im Hub, sonst Profil
  if (!status.damagesDone?.done) return `${base}/schaeden`;
  return `/${id}/profil`;
};

/**
 * Get the next route in the *damage flow* (categories).
 * If everything is done, returns the profile route.
 */
export const getNextDamagePath = ({
  userData,
  carData,
  id,
  vehicleRequirementsState = false,
  carDataPatch,
  damageBasePath,
}) => {
  const merged = carDataPatch
    ? mergeCarDataForSteps(carData, carDataPatch)
    : carData;

  const { status } = checkVehicleSteps(
    userData,
    merged,
    vehicleRequirementsState,
    id,
  );

  if (areAllQuestionStepsDone(status)) {
    return `/${id}/profil`;
  }

  const answered = buildEffectiveDamageAnsweredSet(merged);
  const nachlackierungDone = isNachlackierungDone(merged);
  const paths = getDamagePathMap(
    id,
    resolveDamageBasePath(id, damageBasePath),
  );

  for (const key of DAMAGE_FLOW_ORDER) {
    const done =
      key === "nachlackierung" ? nachlackierungDone : answered.has(key);
    if (!done) return paths[key];
  }

  return `/${id}/profil`;
};

/**
 * Get the next route in the *service history flow*.
 * If everything is done, returns the profile route.
 */
export const getNextServicePath = ({
  userData,
  carData,
  id,
  vehicleRequirementsState = false,
  carDataPatch,
}) => {
  const merged = carDataPatch
    ? mergeCarDataForSteps(carData, carDataPatch)
    : carData;

  const { status } = checkVehicleSteps(
    userData,
    merged,
    vehicleRequirementsState,
    id,
  );

  if (areAllQuestionStepsDone(status)) {
    return `/${id}/profil`;
  }

  const choice = merged?.vehicleFeatures?.service_historie;
  const needsPhotos = choice === "vollständig" || choice === "teilweise";
  const hasPhotos =
    Array.isArray(merged?.serviceHistory) && merged.serviceHistory.length > 0;

  if (!choice) return `/${id}/profil/service-historie`;
  if (needsPhotos && !hasPhotos) {
    return `/${id}/profil/service-historie/fotos`;
  }

  return `/${id}/profil`;
};
